import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";

import { defineMessages, useIntl } from "react-intl";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import { EntityTypeSupportingGroups, MapEntity } from "@mapmycustomers/shared/types/entity";
import { MultiPin } from "@mapmycustomers/shared/types/map";

import {
  monoEntitiesCountTextGetterFactory,
  monoEntitiesPaginationTextGetterFactory,
  MultiPinInformation,
  TextGetters,
} from "@app/component/RecordList";
import useMapView from "@app/component/view/utils/useMapView";
import { RecordList } from "@app/scene/map/components/sidebar/RecordList";
import { fetchGroupRecords } from "@app/scene/map/store/groupMode/actions";
import {
  areGroupRecordsLoading,
  getGroupEntityType,
  getGroupRecords,
  getGroupRecordsTotalCount,
} from "@app/scene/map/store/groupMode/selectors";
import { getMultiPin, getMultiPinAddress } from "@app/scene/map/store/mapMode/selectors";
import { RootState } from "@app/store/rootReducer";
import { mapEntityIdGetter } from "@app/util/map/idGetters";

import GroupMultiPinMode from "../GroupMultiPinMode";
import GroupRecord from "../GroupRecord";

interface Props {
  entityType?: EntityTypeSupportingGroups;
  fetchRecords: typeof fetchGroupRecords.request;
  loading: boolean;
  multiPin?: MultiPin;
  multiPinAddress?: string;
  records: MapEntity[];
  recordsCount: number;
}

const messages = defineMessages({
  placeholder: {
    id: "map.recordsList.groupMultiPin.search.multiPinPlaceholder",
    defaultMessage: "Search records at this location",
    description: "Records list for group multi-pin - search placeholder",
  },
});

const GroupMultiPinRecordList: React.FC<Props> = ({
  entityType,
  fetchRecords,
  loading,
  multiPin,
  multiPinAddress,
  records,
  recordsCount,
}) => {
  const intl = useIntl();

  const {
    viewState: { range, tool },
  } = useMapView();

  const isLassoMode = tool === MapTool.LASSO;
  const isMultiPinMode = !!multiPin && !isLassoMode;

  useEffect(() => {
    // Checking multiPin value here to properly handle switch between different multi-pins
    if (!isLassoMode && multiPin) {
      fetchRecords({});
    }
  }, [fetchRecords, multiPin, isLassoMode]);

  const handleRangeChange = useCallback((range) => fetchRecords({ range }), [fetchRecords]);

  const handleSearch = useCallback((search) => fetchRecords({ search }), [fetchRecords]);

  const textGetters: TextGetters = useMemo(
    () => ({
      countTextGetter: monoEntitiesCountTextGetterFactory(intl, entityType),
      paginationTextGetter: monoEntitiesPaginationTextGetterFactory(intl, entityType),
    }),
    [intl, entityType]
  );

  return isMultiPinMode ? (
    <>
      <GroupMultiPinMode />

      <MultiPinInformation
        multiPinAddress={multiPinAddress}
        recordsCount={recordsCount}
        showAddress={false}
      />

      <RecordList<MapEntity>
        idGetter={mapEntityIdGetter}
        itemComponent={GroupRecord}
        loading={loading}
        onRangeChange={handleRangeChange}
        onSearch={handleSearch}
        range={range}
        records={records}
        searchPlaceholder={intl.formatMessage(messages.placeholder)}
        showCount={false}
        textGetters={textGetters}
        total={recordsCount}
      />
    </>
  ) : null;
};

const mapStateToProps = (state: RootState) => ({
  entityType: getGroupEntityType(state),
  loading: areGroupRecordsLoading(state),
  multiPin: getMultiPin(state),
  multiPinAddress: getMultiPinAddress(state),
  records: getGroupRecords(state),
  recordsCount: getGroupRecordsTotalCount(state),
});

const mapDispatchToProps = {
  fetchRecords: fetchGroupRecords.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupMultiPinRecordList);
